/**
 * Customer proof lifecycle — API client helpers (PL-PROOFLOOP-004/006/007).
 *
 * Wires the four customer-facing proof endpoints to typed TS functions.
 * All helpers:
 *  1. Call the shared axios instance.
 *  2. Unwrap the platform envelope via `unwrap`/`throwAsApiError`.
 *  3. Return camelCase domain shapes (wire is already camelCase — the
 *     platform DTOs project snake_case columns to camelCase server-side).
 *
 * SH-PROOFLOOP-001.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type { ProofDTO, ProofDecisionDTO, ProofThreadNoteDTO } from '@dfq/shared/types/proof';

/** Envelope `data` wrapper for the GET proof endpoint. */
interface ProofEnvelopeData {
  proof: ProofDTO;
}

/** Envelope `data` wrapper for the approve/request-changes endpoints. */
interface ProofDecisionEnvelopeData {
  order: ProofDecisionDTO;
}

/** Envelope `data` wrapper for the feedback-thread endpoint. */
interface ProofThreadEnvelopeData {
  notes: ProofThreadNoteDTO[];
}

/**
 * GET `/api/v1/account/orders/{orderId}/proof`
 *
 * Returns the latest-round proof for the order with `proofS3Key` minted
 * into a presigned URL at read time.
 *
 * Throws `ApiError` with `status: 404` for a missing order or a missing
 * proof, `status: 403` for a foreign order.
 */
export async function fetchProof(orderId: number): Promise<ProofDTO> {
  try {
    const response = await apiClient.get<ApiEnvelope<ProofEnvelopeData>>(
      `/account/orders/${orderId}/proof`,
    );
    const data = unwrap(response.data, response.status);
    return data.proof;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/account/orders/{orderId}/proof/approve`
 *
 * Transitions the order from `awaiting_customer_approval` →
 * `approved_for_print` and records a `proof_approvals` row
 * (decision = approved).
 *
 * Throws `ApiError` with `status: 409` when the order is not awaiting
 * approval.
 */
/**
 * Approve the proof, which also collects the outstanding balance.
 *
 * `paymentMethodId` is sent only when the customer is retrying after a decline
 * — the balance otherwise goes to the card that paid the deposit. A refusal
 * comes back as a 402 carrying `decline_code`, which the caller is expected to
 * present as a payment problem rather than an artwork one.
 */
export async function approveProof(
  orderId: number,
  paymentMethodId?: string,
): Promise<ProofDecisionDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<ProofDecisionEnvelopeData>>(
      `/account/orders/${orderId}/proof/approve`,
      paymentMethodId === undefined ? undefined : { paymentMethodId },
    );
    const data = unwrap(response.data, response.status);
    return data.order;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/account/orders/{orderId}/proof/request-changes`
 *
 * Body: `{ reason: string }`.
 *
 * Transitions the order from `awaiting_customer_approval` → `artwork_issue`,
 * creates a customer-visible PrepressNote, and records a `proof_approvals`
 * row (decision = revision_requested).
 *
 * Throws `ApiError` with `status: 409` when the order is not awaiting
 * approval and `status: 422` when `reason` fails validation.
 */
export async function requestProofChanges(
  orderId: number,
  reason: string,
): Promise<ProofDecisionDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<ProofDecisionEnvelopeData>>(
      `/account/orders/${orderId}/proof/request-changes`,
      { reason },
    );
    const data = unwrap(response.data, response.status);
    return data.order;
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * GET `/api/v1/account/orders/{orderId}/proof/thread`
 *
 * Returns the customer-visible prepress-note feedback thread for an
 * order, chronological (oldest first). Staff-internal notes are excluded
 * at the repository query level.
 *
 * Throws `ApiError` with `status: 403` for a foreign order, `status: 404`
 * for a missing order.
 */
export async function fetchProofThread(orderId: number): Promise<ProofThreadNoteDTO[]> {
  try {
    const response = await apiClient.get<ApiEnvelope<ProofThreadEnvelopeData>>(
      `/account/orders/${orderId}/proof/thread`,
    );
    const data = unwrap(response.data, response.status);
    return data.notes;
  } catch (err) {
    throwAsApiError(err);
  }
}
